import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from 'uuid';
import { Session } from "@supabase/supabase-js";

export type ChatMessage = {
  role: 'user' | 'assistant';
  message: string;
  createdAt: string;
};

type UserData = {
  id: string;
  email: string | undefined;
};

// Database operations
const userOperations = {
  async createUser({ id, email }: UserData) {
    console.log('Creating user:', { id, email });
    const { data, error } = await supabase
      .from('User')
      .insert({ id, email })
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Error creating user:', error);
      throw new Error('Failed to create user');
    }
    
    return data;
  },

  async findUser(userId: string) {
    console.log('Finding user:', userId);
    const { data, error } = await supabase
      .from('User')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error finding user:', error);
      throw new Error('Failed to find user');
    }
    
    return data;
  },

  async createConversation(userId: string, title: string) {
    const { data, error } = await supabase
      .from('Conversation')
      .insert({
        userId,
        title,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating conversation:', error);
      throw new Error('Failed to create conversation');
    }

    return data;
  },

  async saveChat(userId: string, role: 'user' | 'assistant', message: string, conversationId: string) {
    console.log('Saving chat message:', { userId, role, message, conversationId });
    const { error } = await supabase
      .from('Chat')
      .insert({
        id: uuidv4(),
        userId,
        role,
        message,
        conversationId,
      });
    
    if (error) {
      console.error('Error saving message:', error);
      throw new Error(`Failed to save message: ${error.message}`);
    }
  }
};

export const useChat = (session: Session | null, conversationId?: string) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Load messages from local storage on initial mount
  useEffect(() => {
    if (session?.user && conversationId) {
      const storageKey = `chat_${session.user.id}_${conversationId}`;
      const storedMessages = localStorage.getItem(storageKey);
      if (storedMessages) {
        setMessages(JSON.parse(storedMessages));
      }
    }
  }, [session?.user?.id, conversationId]);

  // Save messages to local storage whenever they change
  useEffect(() => {
    if (session?.user && conversationId && messages.length > 0) {
      const storageKey = `chat_${session.user.id}_${conversationId}`;
      localStorage.setItem(storageKey, JSON.stringify(messages));
    }
  }, [messages, session?.user?.id, conversationId]);

  const ensureUserExists = async (userId: string, email: string | undefined) => {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const existingUser = await userOperations.findUser(userId);
      
      if (!existingUser) {
        console.log('User not found, creating new user');
        await userOperations.createUser({ id: userId, email });
      }
    } catch (error: any) {
      console.error('Error in ensureUserExists:', error);
      throw new Error(error.message || 'Failed to verify or create user');
    }
  };

  const sendMessage = async (userMessage: string) => {
    if (!userMessage.trim() || isLoading) return;
    
    if (!session?.user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to send messages",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      let currentConversationId = conversationId;
      
      // If no conversation exists, create one
      if (!currentConversationId) {
        const conversation = await userOperations.createConversation(
          session.user.id,
          userMessage.substring(0, 50) + "..."
        );
        currentConversationId = conversation.id;
      }

      // Add and save user message
      const newUserMessage = { 
        role: 'user' as const, 
        message: userMessage,
        createdAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, newUserMessage]);
      await userOperations.saveChat(session.user.id, 'user', userMessage, currentConversationId);

      // Get AI response
      const response = await supabase.functions.invoke('chat', {
        body: { message: userMessage }
      });

      if (response.error) {
        throw response.error;
      }

      // Add and save AI response
      const aiResponse = response.data.message;
      const newAiMessage = { 
        role: 'assistant' as const, 
        message: aiResponse,
        createdAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, newAiMessage]);
      await userOperations.saveChat(session.user.id, 'assistant', aiResponse, currentConversationId);
    } catch (error: any) {
      console.error('Error in chat interaction:', error);
      toast({
        title: "Error in chat",
        description: error.message || "Failed to send message",
        variant: "destructive",
      });
      
      // Remove the user message if there was an error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    setMessages,
    isLoading,
    sendMessage,
  };
};